// Mobile Mail — List view (Gmail Mobile Web 风格).
// Mobile Mail v1.2 (2026-09-14): controlled by Page-level state (items/page/hasMore).
//   useMailMessages moved up to MobileMailPage (because page is owned there).
//   AppBar adds RefreshCw button (left of Compose) per user prompt §1.
//   Bottom sentinel + load-more / loadmore-end per user prompt §2.
// No business logic duplication — Page-level state, List = pure presentational.

import { MailPlus, Menu, RefreshCw } from "lucide-react";
import { MobileCard } from "../components/MobileCard";
import type { MessageV2View } from "../../modules/mail-v2/api/mappers";
import type { FolderId } from "./MobileFolderDrawer";

interface Props {
  items: MessageV2View[];
  loading: boolean; // 首次加载 (遮盖整页)
  error: string | null;
  loadingMore: boolean;
  hasMore: boolean;
  isRefreshing: boolean;
  accountId: string;
  folderLabel: string;
  emptyText: string;
  onSelectMessage: (msg: MessageV2View) => void;
  onCompose: () => void;
  onRefresh: () => void;
  onOpenFolderMenu: () => void;
  loadMoreRef: React.RefObject<HTMLDivElement>;
}

const EMPTY_TEXT: Record<FolderId, string> = {
  inbox: "受信メールはありません",
  sent: "送信済みメールはありません",
  drafts: "下書きはありません",
  all: "メールはありません",
  trash: "ゴミ箱は空です",
  spam: "迷惑メールはありません",
};

function formatDateShort(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleString("ja-JP", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleString("ja-JP", { month: "numeric", day: "numeric" });
}

export const MobileMailList = ({
  items,
  loading,
  error,
  loadingMore,
  hasMore,
  isRefreshing,
  accountId,
  folderLabel,
  emptyText,
  onSelectMessage,
  onCompose,
  onRefresh,
  onOpenFolderMenu,
  loadMoreRef,
}: Props) => {
  return (
    <>
      <header className="mobile-appbar">
        <div className="mobile-appbar-left">
          <button
            type="button"
            className="mobile-appbar-icon-btn"
            onClick={onOpenFolderMenu}
            aria-label="メニュー"
          >
            <Menu size={20} />
          </button>
        </div>
        <h1 className="mobile-appbar-title">{folderLabel}</h1>
        <div className="mobile-appbar-actions">
          <button
            type="button"
            className="mobile-appbar-icon-btn"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="更新"
            title={isRefreshing ? "更新中..." : "更新"}
          >
            <RefreshCw size={20} className={isRefreshing ? "spin" : ""} />
          </button>
          <button
            type="button"
            className="mobile-appbar-icon-btn"
            onClick={onCompose}
            aria-label="新規メール"
          >
            <MailPlus size={20} />
          </button>
        </div>
      </header>

      <main className="mobile-mail-content">
        {!accountId ? (
          <div style={{ padding: 16 }}>
            <MobileCard>
              <p
                style={{
                  textAlign: "center",
                  color: "var(--mobile-text-sub, #6b7280)",
                  padding: 18,
                }}
              >
                メールアカウントが設定されていません。PC から設定してください。
              </p>
            </MobileCard>
          </div>
        ) : error && items.length === 0 ? (
          <div style={{ padding: 16 }}>
            <MobileCard>
              <p style={{ color: "#dc2626", fontSize: 12, padding: 18 }}>{error}</p>
            </MobileCard>
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <RefreshCw className="spin" size={22} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 16 }}>
            <MobileCard>
              <p
                style={{
                  textAlign: "center",
                  color: "var(--mobile-text-sub, #6b7280)",
                  padding: 18,
                }}
              >
                {emptyText}
              </p>
            </MobileCard>
          </div>
        ) : (
          <div className="mobile-mail-list">
            {items.map((m) => (
              <button
                type="button"
                key={m.id}
                className={`mobile-mail-row ${m.isRead ? "" : "unread"}`}
                onClick={() => onSelectMessage(m)}
              >
                <div className="mobile-mail-row-top">
                  <span className="mobile-mail-sender">
                    {m.senderName || m.senderEmail}
                  </span>
                  <span className="mobile-mail-date">
                    {formatDateShort(m.receivedAt)}
                  </span>
                </div>
                <div className="mobile-mail-subject">
                  {m.subject || "(件名なし)"}
                </div>
                <div className="mobile-mail-preview">
                  {m.preview || m.bodyText || ""}
                </div>
              </button>
            ))}
            <div ref={loadMoreRef} className="mobile-mail-sentinel" />
            {loadingMore && (
              <div className="mobile-mail-loadmore">さらに読み込み中...</div>
            )}
            {!hasMore && items.length > 0 && (
              <div className="mobile-mail-loadmore-end">
                これ以上メールはありません
              </div>
            )}
            {error && items.length > 0 && (
              <div className="mobile-mail-loadmore">
                {error}
              </div>
            )}
          </div>
        )}
      </main>
    </>
  );
};
